import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../Auth/AuthProvider.js";
import { backend_url } from "shared/config.js";
import { PlusCircle, X } from "lucide-react";
import CreatePostForm from "./CreatePostForm.js";

const InlineCreatePost = ({ onPostCreated }) => {
  const { user, userInfo, accessToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleCreatePost = async (submissionData) => {
    try {
      const res = await fetch(
        backend_url + "/create/question_and_ai_response?uid=" + user.uid,
        {
          method: "POST",
          body: JSON.stringify(submissionData),
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer: " + accessToken,
          },
        }
      );

      if (res.ok) {
        const responseData = await res.json();
        console.log(responseData);
        // Reset form state
        setIsExpanded(false);

        // Notify parent component to refresh posts
        if (onPostCreated) {
          onPostCreated();
        }

        navigate(`/post/${responseData.post.id}`);

        return true; // Indicate success to CreatePostForm
      }

      return false; // Indicate failure
    } catch (e) {
      console.error("Error creating post: ", e);
      return false;
    }
  };

  const handleCancel = () => {
    setIsExpanded(false);
  };

  if (!user) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 mx-4">
        <div className="text-center text-gray-500">
          <p className="mb-2">Want to ask a question?</p>
          <p className="text-sm">
            <a
              href="/login"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Sign in
            </a>{" "}
            to start a conversation
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mx-4 mt-4 overflow-hidden transition-all duration-200">
      {!isExpanded ? (
        // Collapsed state - Quick ask
        <div
          onClick={() => setIsExpanded(true)}
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
              <img
                src={
                  userInfo?.profilePicture ||
                  `${backend_url}/default_profile_pic2.jpeg`
                }
                className="w-full h-full object-cover"
                alt="Your avatar"
                onError={(e) => {
                  e.target.src = backend_url + "/default_profile_pic2.jpeg";
                }}
              />
            </div>
            <div className="flex-1">
              <div className="bg-gray-100 rounded-full px-4 py-2 text-gray-500 hover:bg-gray-200 transition-colors duration-200">
                Ask
              </div>
            </div>
            <PlusCircle className="w-6 h-6 text-blue-600" />
          </div>
        </div>
      ) : (
        // Expanded state - CreatePostForm integration
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Ask a Question
            </h3>
            <button
              type="button"
              onClick={handleCancel}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <CreatePostForm
            user={user}
            onSubmit={handleCreatePost}
            onCancel={handleCancel}
            placeholder="Describe your question in detail... (Markdown supported)"
            formType={CreatePostForm.FORM_TYPES.QUESTION}
            showAdvanced={true}
            isTopLevel={true}
            maxLength={1000}
            buttonText="Ask Question"
          />
        </div>
      )}
    </div>
  );
};

export default InlineCreatePost;
